'use client';

import React, { useMemo, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';

import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useStoresList } from '@/lib/api/stores';
import { MultiSelect } from '@/components/ui/multi-select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
    SheetClose,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';

import { useStatsFilters } from '../../context/StatsFiltersContext';
import { TimezoneSelector } from './TimezoneSelector';
import { CurrencySelector } from './CurrencySelector';

function FieldLabel({ children }: { children: React.ReactNode }) {
    return (
        <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {children}
        </label>
    );
}

export function FilterBar() {
    const {
        selectedDates,
        selectDates,
        granularities,
        selectedGranularity,
        selectGranularity,
        selectedStores,
        selectStores,
        selectedTimezone,
        selectTimezone,
        selectedCurrency,
        selectCurrency,
        currencies,
    } = useStatsFilters();
    const { t } = useTranslation('analytics');

    const isMobile = useIsMobile();
    const [sheetOpen, setSheetOpen] = useState(false);

    const { data: storesData } = useStoresList({ pagination: { page: 1, pageSize: 100 } });
    const storeOptions = (storesData?.stores || []).map((s) => ({ label: s.name, value: s.id }));

    const activeCount = useMemo(() => {
        let count = 0;
        if (selectedStores.length > 0) count += 1;
        if (selectedTimezone && selectedTimezone !== 'UTC') count += 1;
        if (selectedGranularity && selectedGranularity !== 'daily') count += 1;
        if (currencies.length > 1 && selectedCurrency) count += 1;
        return count;
    }, [selectedStores, selectedTimezone, selectedGranularity, selectedCurrency, currencies]);

    if (isMobile) {
        return (
            <div className="flex w-full items-center gap-2">
                <div className="min-w-0 flex-1">
                    <DateRangePicker
                        value={selectedDates as any}
                        onChange={(range) => {
                            if (range?.from && range?.to) {
                                selectDates({ from: range.from, to: range.to });
                            }
                        }}
                    />
                </div>

                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                    <SheetTrigger asChild>
                        <Button variant="outline" className="relative shrink-0">
                            <SlidersHorizontal className="size-4" />
                            <span>{t('filters.title')}</span>
                            {activeCount > 0 && (
                                <Badge variant="default" className="ml-1 h-5 rounded-full px-1.5 text-[10px]">
                                    {activeCount}
                                </Badge>
                            )}
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto p-0">
                        <SheetHeader className="border-b">
                            <SheetTitle>{t('filters.title')}</SheetTitle>
                            <SheetDescription>{t('filters.configure')}</SheetDescription>
                        </SheetHeader>
                        <div className="space-y-5 p-4">
                            <div className="space-y-2">
                                <FieldLabel>{t('filters.stores')}</FieldLabel>
                                <MultiSelect
                                    placeholder={t('filters.allStores')}
                                    options={storeOptions}
                                    selected={selectedStores}
                                    onChange={selectStores}
                                    className="min-h-10"
                                />
                            </div>

                            <div className="space-y-2">
                                <FieldLabel>{t('filters.timezone')}</FieldLabel>
                                <TimezoneSelector
                                    selectedTimezone={selectedTimezone}
                                    onTimezoneChange={selectTimezone}
                                />
                            </div>

                            <div className="space-y-2">
                                <FieldLabel>{t('filters.granularity')}</FieldLabel>
                                <Select value={selectedGranularity} onValueChange={(v) => selectGranularity(v as any)}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={t('filters.granularity')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {granularities.map((g) => (
                                            <SelectItem key={g} value={g}>
                                                {g[0].toUpperCase() + g.slice(1)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {currencies.length > 1 && (
                                <div className="space-y-2">
                                    <FieldLabel>{t('filters.currency')}</FieldLabel>
                                    <Select value={selectedCurrency} onValueChange={selectCurrency}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {currencies.map((currency) => (
                                                <SelectItem key={currency} value={currency}>
                                                    {currency}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>
                        <SheetFooter className="border-t">
                            <SheetClose asChild>
                                <Button className="w-full">{t('filters.apply')}</Button>
                            </SheetClose>
                        </SheetFooter>
                    </SheetContent>
                </Sheet>
            </div>
        );
    }

    return (
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
                <DateRangePicker
                    value={selectedDates as any}
                    onChange={(range) => {
                        if (range?.from && range?.to) {
                            selectDates({ from: range.from, to: range.to });
                        }
                    }}
                />

                <span className="hidden text-sm font-medium text-muted-foreground sm:block">{t('filters.stores')}</span>
                <div className="w-full sm:min-w-[220px] sm:w-auto">
                    <MultiSelect
                        placeholder={t('filters.stores')}
                        options={storeOptions}
                        selected={selectedStores}
                        onChange={selectStores}
                        className="min-h-9"
                    />
                </div>

                <span className="hidden text-sm font-medium text-muted-foreground sm:block">{t('filters.timezone')}</span>
                <TimezoneSelector
                    selectedTimezone={selectedTimezone}
                    onTimezoneChange={selectTimezone}
                />

                <span className="hidden text-sm font-medium text-muted-foreground sm:block">{t('filters.granularity')}</span>
                <Select value={selectedGranularity} onValueChange={(v) => selectGranularity(v as any)}>
                    <SelectTrigger className="h-9 w-full sm:w-[140px]">
                        <SelectValue placeholder={t('filters.granularity')} />
                    </SelectTrigger>
                    <SelectContent>
                        {granularities.map((g) => (
                            <SelectItem key={g} value={g}>{g[0].toUpperCase() + g.slice(1)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <CurrencySelector
                    selectedCurrency={selectedCurrency}
                    onCurrencyChange={selectCurrency}
                    currencies={currencies}
                />
            </div>
        </div>
    );
}
